import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import Table from '../components/common/Table';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Pagination from '../components/common/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDate, getServiceTypeLabel, formatCurrency } from '../utils/helpers';
import { SERVICE_TYPES, PAYMENT_TYPES } from '../utils/constants';

const Invoices = () => {
  const navigate = useNavigate();
  const { invoices, loadingInvoices } = useApp();
  
  const [filters, setFilters] = useState({
    search: '',
    serviceType: '',
    paymentType: ''
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const filteredInvoices = useMemo(() => invoices.filter(inv => {
    const matchesSearch = !filters.search || 
      inv.customerName?.toLowerCase().includes(filters.search.toLowerCase()) ||
      inv.invoiceNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
      inv.phone?.includes(filters.search);
    const matchesService = !filters.serviceType || inv.serviceType === filters.serviceType;
    const matchesPayment = !filters.paymentType || inv.paymentType === filters.paymentType;
    
    return matchesSearch && matchesService && matchesPayment;
  }), [invoices, filters]);

  const { page, setPage, pageSize, setPageSize, total, totalPages, pagedItems } = usePagination(
    filteredInvoices,
    { persistKey: 'invoices', resetKey: `${filters.search}|${filters.serviceType}|${filters.paymentType}` }
  );

  // Calculate totals
  const totalRevenue = filteredInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Lambar',
      render: (value) => (
        <span className="font-mono font-medium text-primary-600">{value}</span>
      )
    },
    {
      key: 'customerName',
      label: 'Macmiilka',
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{row.phone}</p>
        </div>
      )
    },
    {
      key: 'serviceType',
      label: 'Adeegga',
      render: (value) => (
        <span className="text-sm">{getServiceTypeLabel(value)}</span>
      )
    },
    {
      key: 'paymentType',
      label: 'Lacagta',
      render: (value) => (
        <span className="px-2 py-1 bg-secondary-50 text-secondary-700 rounded-full text-xs">
          {value}
        </span>
      )
    },
    {
      key: 'totalAmount',
      label: 'Wadarta',
      render: (value) => (
        <span className="font-bold text-green-600">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'createdByEmail',
      label: 'Sameeyay',
      render: (value) => (
        <span className="text-xs text-gray-600 truncate max-w-[120px] inline-block" title={value || ''}>
          {value || '—'}
        </span>
      )
    },
    {
      key: 'createdAt',
      label: 'Taariikhda',
      render: (value) => formatDate(value)
    }
  ];

  return (
    <div className="space-y-4">
      <PageHero
        accent="blue"
        eyebrow="Lacagaha la xaqiijiyay"
        title="Invoices"
        subtitle="Dhammaan invoice-yada la sameeyay"
        stats={[
          { label: 'Wadarta Guud', value: formatCurrency(totalRevenue), hint: 'Dakhliga invoice-yada' },
          { label: 'Invoices', value: filteredInvoices.length },
          { label: 'Celcelis', value: formatCurrency(filteredInvoices.length > 0 ? totalRevenue / filteredInvoices.length : 0) },
          { label: 'Ugu yar', value: formatCurrency(filteredInvoices.length > 0 ? Math.min(...filteredInvoices.map(i => i.totalAmount || 0)) : 0) }
        ]}
      />

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Raadi lambar, magac, telefoon..."
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
          />
          <Select
            name="serviceType"
            value={filters.serviceType}
            onChange={handleFilterChange}
            options={SERVICE_TYPES}
            placeholder="Nooca Adeegga"
          />
          <Select
            name="paymentType"
            value={filters.paymentType}
            onChange={handleFilterChange}
            options={PAYMENT_TYPES}
            placeholder="Nooca Lacagta"
          />
        </div>
      </Card>

      <Card padding={false}>
        <Table
          columns={columns}
          data={pagedItems}
          loading={loadingInvoices}
          onRowClick={(row) => navigate(`/admin/invoices/${row.id}`)}
          emptyMessage="Invoice lama helin"
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>
    </div>
  );
};

export default Invoices;